import { forwardRef } from "react";

type SmartImageProps = Omit<React.ImgHTMLAttributes<HTMLImageElement>, "srcSet"> & {
  src: string;
  alt: string;
  width: number;
  height: number;
  /** Comma-separated `sizes` attribute. Defaults cover most full-bleed/grid layouts. */
  sizes?: string;
  /** Pixel widths to emit in the srcset. */
  widths?: number[];
  /** Hint for the upstream proxy. Most callers can leave this alone. */
  quality?: number;
  /** Skip the AVIF/WebP <picture> wrapping — useful when a parent already did it. */
  bareImg?: boolean;
};

const DEFAULT_WIDTHS = [400, 640, 800, 1200, 1600];

function buildSrc(src: string, w: number, q: number, fmt: "avif" | "webp" | "auto"): string {
  const url = `/api/img?url=${encodeURIComponent(src)}&w=${w}&q=${q}&fmt=${fmt}`;
  return url;
}

function buildSrcSet(src: string, widths: number[], q: number, fmt: "avif" | "webp" | "auto"): string {
  return widths.map((w) => `${buildSrc(src, w, q, fmt)} ${w}w`).join(", ");
}

/**
 * Renders an image through our `/api/img` proxy with AVIF + WebP srcsets.
 * Falls back to a plain JPEG/PNG `<img>` for clients that don't accept either.
 */
const SmartImage = forwardRef<HTMLImageElement, SmartImageProps>(function SmartImage(
  {
    src,
    alt,
    width,
    height,
    sizes = "(max-width: 768px) 100vw, (max-width: 1280px) 50vw, 800px",
    widths = DEFAULT_WIDTHS,
    quality = 75,
    bareImg,
    loading = "lazy",
    decoding = "async",
    className,
    ...rest
  },
  ref,
) {
  // Cap srcset entries at the requested width so we never serve absurdly large
  // images for tiny thumbnails.
  const cap = Math.max(width * 2, 800);
  const filteredWidths = Array.from(new Set(widths.filter((w) => w <= cap))).sort((a, b) => a - b);
  const fallbackWidth = filteredWidths[filteredWidths.length - 1] ?? width;

  const img = (
    <img
      ref={ref}
      src={buildSrc(src, fallbackWidth, quality, "auto")}
      srcSet={buildSrcSet(src, filteredWidths, quality, "auto")}
      sizes={sizes}
      width={width}
      height={height}
      alt={alt}
      loading={loading}
      decoding={decoding}
      className={className}
      {...rest}
    />
  );

  if (bareImg) return img;

  return (
    <picture>
      <source
        type="image/avif"
        srcSet={buildSrcSet(src, filteredWidths, quality, "avif")}
        sizes={sizes}
      />
      <source
        type="image/webp"
        srcSet={buildSrcSet(src, filteredWidths, quality, "webp")}
        sizes={sizes}
      />
      {img}
    </picture>
  );
});

export default SmartImage;
